import { PaymentStatus } from './../models/payment-status.enum';
import { Injectable } from '@angular/core';
import { Payment } from '../models/payment';
import { PaymentHttpService } from './payment.http.service';
import { Observable } from 'rxjs/Observable';
import { LogService } from '../../shared/log/log.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { PaymentStatusUtils } from 'app/event/utils/payment-status-utils';
import { PaymentType } from '../models/payment-type';
import { catchError, map, take, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { BulkActionResponse } from '../models/bulk-action-response';
import { PaymentSummaryStore } from './payment-summary/payment-summary.store';
import { PaymentSummary } from '../models/payment-summary/payment-summary';

@Injectable()
export class PaymentStore {

  // This is the same as BehaviorSubject but without a default value
  private paymentList: ReplaySubject<List<Payment>> = new ReplaySubject<List<Payment>>(1);

  private latestFinalPayment: ReplaySubject<Payment> = new ReplaySubject<Payment>(1);
  private latestVendorFinalPayments: ReplaySubject<Payment[]> = new ReplaySubject<Payment[]>(1);

  private latestPrepayment: ReplaySubject<Payment> = new ReplaySubject<Payment>(1);
  private latestVendorPrepayments: ReplaySubject<Payment[]> = new ReplaySubject<Payment[]>(1);
  private latestVendorPrepaymentsList: ReplaySubject<List<Payment>> = new ReplaySubject<List<Payment>>(1);

  private finalPaymentStatusList: ReplaySubject<Payment[]> = new ReplaySubject<Payment[]>(1);

  private latestVendorInitialPayment: ReplaySubject<Payment> = new ReplaySubject<Payment>(1);
  private latestInitialPayments: ReplaySubject<Payment[]> = new ReplaySubject<Payment[]>(1);

  private _calculatedPaymentWithTaxByEventPartyId: Map<number, number> = new Map<number, number>();
  private _calculatedPaymentWithTaxByEventPartyId$ = new BehaviorSubject<Map<number, number>>(this._calculatedPaymentWithTaxByEventPartyId);

  private readonly addPaymentStatuses = [
    PaymentType.PREPAYMENT,
    PaymentType.PAID_DIRECT,
    PaymentType.EVENT_CANCELLED,
  ];

  constructor(private paymentHttpService: PaymentHttpService,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
              private paymentSummaryStore: PaymentSummaryStore) {
  }

  addPayment(payment: Payment): Observable<any> {
    return this.addPaymentHelper(payment)
      .catch(error => {
        if (error.includes('Unable to request final payment')) {
          this.flashMessagesService.error('MESSAGES.ERROR.OOIV_HEALTH', error);
        } else if (error.includes('Legal Entity data for agency')) {
          this.flashMessagesService.error('MESSAGES.ERROR.MISSING_LEGAL_ENTITY_AGENCY', `Error adding Payment: ${error}`);
        } else if (error.includes('Legal Entity data for vendor')) {
          this.flashMessagesService.error('MESSAGES.ERROR.MISSING_LEGAL_ENTITY_VENDOR', `Error adding Payment: ${error}`);
        } else if (error.includes('Missing information in legal entity table')) {
          this.flashMessagesService.error('MESSAGES.ERROR.LEGAL_ENTITY_MISSING_INFO', `Error adding Payment: ${error}`);
        } else if (error.includes('Invalid Tax ID')) {
          this.flashMessagesService.error('MESSAGES.ERROR.INVALID_VAT_ID', `Error adding Payment: ${error}`);
        } else if (error.includes('Invalid Tax ID')) {
          this.flashMessagesService.error('MESSAGES.ERROR.INVALID_VAT_ID', error);
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        }
        return Observable.throw(error);
      })
      .map(result => {
        if (payment.type === PaymentType.PREPAYMENT) {
          this.flashMessagesService.success('EVENT.PREPAYMENT.PREPAYMENT_SAVED');
        } else {
          this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        }
        return result;
      });
  }

  updateFinalPayment(payment: Payment): Observable<any> {
    return this.updatePaymentHelper(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_PAYMENT', `Error updating Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        return result;
      });
  }

  addBulkActionPayment(payment: Payment): Observable<any> {
    return this.addPaymentHelper(payment)
      .catch(error => {
        if (error.includes('Unable to request final payment')) {
          this.flashMessagesService.error('MESSAGES.ERROR.OOIV_HEALTH', error);
        } else if (error.includes('Legal Entity data for agency')) {
          this.flashMessagesService.error('MESSAGES.ERROR.MISSING_LEGAL_ENTITY_AGENCY', `Error adding Payment: ${error}`);
        } else if (error.includes('Legal Entity data for vendor')) {
          this.flashMessagesService.error('MESSAGES.ERROR.MISSING_LEGAL_ENTITY_VENDOR', `Error adding Payment: ${error}`);
        } else if (error.includes('Invalid Tax ID')) {
          this.flashMessagesService.error('MESSAGES.ERROR.INVALID_VAT_ID', error);
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        }
        return Observable.throw(error);
      })
      .map(result => {
        return result;
      });
  }

  updateBulkActionPayment(payment: Payment): Observable<any> {
    return this.updatePaymentHelper(payment)
      .catch(error => {
        return Observable.throw(error);
      })
      .map(result => {
        return result;
      });
  }

  cancelNewPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.cancelNew(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        if (payment.type === PaymentType.PREPAYMENT) {
          this.flashMessagesService.success('EVENT.PREPAYMENT.PREPAYMENT_SAVED');
        } else {
          this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        }
        return result;
      });
  }

  cancelExistingPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.cancelExisting(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_PAYMENT', `Error updating Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        return result;
      });
  }

  get payments(): Observable<List<Payment>> {
    return this.paymentList.asObservable();
  }

  getVendorPayments(eventPartyId: number): Observable<List<Payment>> {
    return this.paymentList.map((filteredList: List<Payment>) => {
      if (filteredList == null) {
        return null;
      }
      return filteredList.filter((filtered: Payment) => filtered.eventPartyId === eventPartyId).toList();
    });
  }

  /**
   *
   * @returns Observable of Payment[] without REQUEST_REJECTED payments
   */
  getLatestFinalPayment(): Observable<Payment> {
    return this.latestFinalPayment.asObservable();
  }

  /**
   *
   * @returns Observable of Payment[] without REQUEST_REJECTED payments
   */
  getLatestVendorFinalPayments(): Observable<Payment[]> {
    return this.latestVendorFinalPayments.asObservable().pipe(
      // Filter out REQUEST_REJECTED payments
      map((payments: Payment[]) => this.filterNonRequestRejectedPayments(payments))
    );
  }

  /**
   *
   * @param eventPartyId
   * @returns Observable of Payment without REQUEST_REJECTED payments
   */
  getVendorFinalPayment(eventPartyId: number): Observable<Payment> {
    return this.latestVendorFinalPayments
      .map(payments => {
        // Filter out REQUEST_REJECTED payments
        payments = this.filterNonRequestRejectedPayments(payments);
        return payments != null ? payments.filter(payment => payment.eventPartyId === eventPartyId)[0] : null;
      });
  }

  private filterNonRequestRejectedPayments(payments: Payment[]): Payment[] {
    if (payments == null) {
      return null;
    }
    const filteredPayments = payments.filter(payment => payment.status !== PaymentStatus.REQUEST_REJECTED);
    if (filteredPayments.length === 0) {
      return null;
    }
    return filteredPayments;
  }

  /**
   *
   * @param eventPartyId
   * @returns Observable of Payment that may be in REQUEST_REJECTED status
   */
  getVendorFinalPaymentIncludeRequestRejected(eventPartyId: number): Observable<Payment> {
    return this.latestVendorFinalPayments
      .map(payments => {
        if (payments == null) {
          return null;
        }

        return payments.filter(payment => payment.eventPartyId === eventPartyId)[0];
      });
  }

  loadPayments(eventId: number): Observable<List<Payment>> {
    return this.paymentHttpService.getPayments(eventId)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PAYMENTS'));
        this.logService.error(`Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Payment>) => {
        this.paymentList.next(result);
        return result;
      });
  }

  loadPaymentsByParty(eventId: number, eventPartyId: number): Observable<List<Payment>> {
    return this.paymentHttpService.getPaymentsByParty(eventId, eventPartyId)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PAYMENTS'));
        this.logService.error(`Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Payment>) => {
        this.paymentList.next(result);
        return result;
      });
  }

  loadLatestFinalPayment(eventId: number): Observable<Payment[]> {
    return this.paymentHttpService.getLatestVendorFinalPayment(eventId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_LATEST_PAYMENT'));
      this.logService.error(`Error getting last Payments: ${error}`);
      return Observable.throw(error);
    }).map((payments: Payment[]) => {
      // This subject may include REQUEST_REJECTED payments
      this.latestVendorFinalPayments.next(payments);

      // Filter out REQUEST_REJECTED payments for latestFinalPayment
      const filteredPayments = this.filterNonRequestRejectedPayments(payments);
      if (filteredPayments && filteredPayments.length === 1) {
        this.latestFinalPayment.next(filteredPayments[0]);
      } else {
        this.latestFinalPayment.next(null);
      }

      return payments;
    });
  }

  loadLatestFinalPaymentByParty(eventId: number, eventPartyId: number): Observable<Payment[]> {
    return this.paymentHttpService.getLatestVendorFinalPaymentByParty(eventId, eventPartyId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_LATEST_PAYMENT'));
      this.logService.error(`Error getting last Payments: ${error}`);
      return Observable.throw(error);
    }).map((payments: Payment[]) => {
      // This subject may include REQUEST_REJECTED payments
      this.latestVendorFinalPayments.next(payments);

      // Filter out REQUEST_REJECTED payments for latestFinalPayment
      const filteredPayments = this.filterNonRequestRejectedPayments(payments);
      this.latestFinalPayment.next(null);
      if (filteredPayments && filteredPayments.length === 1) {
        this.latestFinalPayment.next(filteredPayments[0]);
      }
      return payments;
    });
  }

  get commissionTotal(): Observable<number> {
    return this.payments.map(payments => {
      return payments
        .filter(payment => !this.isPaymentRejected(payment))
        .map(payment => payment.commissionAmount)
        .reduce((prev: number, current: number) => !!current ? prev + current : prev, 0);
    });
  }

  get containsRejectedPayments(): Observable<boolean> {
    return this.payments.map(payments => payments.some(this.isPaymentRejected));
  }

  /**
   * Gets the payment with the latest created date
   */
  get currentPayment(): Observable<Payment> {
    return this.payments.map((list: List<Payment>) => {
      return list.max((payment1: Payment, payment2: Payment) => {
        if (payment1.createdDate < payment2.createdDate) {
          return -1;
        } else if (payment1.createdDate > payment2.createdDate) {
          return 1;
        } else {
          return 0;
        }
      });
    });
  }

  /**
   * Gets the payment with the latest created date
   */
  getVendorCurrentPayment(eventPartyId: number): Observable<Payment> {
    return this.getVendorPayments(eventPartyId)
      .map(payments => payments.filter(payment => payment.eventPartyId === eventPartyId))
      .map((list: List<Payment>) => {
      return list.max((payment1: Payment, payment2: Payment) => {
        if (payment1.createdDate < payment2.createdDate) {
          return -1;
        } else if (payment1.createdDate > payment2.createdDate) {
          return 1;
        } else {
          return 0;
        }
      });
    });
  }

  /**
   * Returns true if the current payment exists and does not have a rejected or error status
   * @returns {Observable<R>}
   */
  get isCurrentPaymentValid(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return !!lastPayment && !this.isPaymentRejected(lastPayment);
    });
  }

  /**
   * Returns true if the current vendor payment exists and does not have a rejected or error status
   * @returns {Observable<R>}
   */
  isCurrentVendorPaymentValid(eventPartyId: number): Observable<boolean> {
    return this.getVendorCurrentPayment(eventPartyId).map((lastPayment: Payment) => {
      return !!lastPayment && !this.isPaymentRejected(lastPayment);
    });
  }

  isVendorPaymentError(eventPartyId: number): Observable<boolean> {
    return this.getVendorCurrentPayment(eventPartyId).map((lastPayment: Payment) => {
      return !!lastPayment && PaymentStatusUtils.isError(lastPayment.status);
    });
  }

  public isPaymentRejected(payment: Payment): boolean {
    if (payment) {
      return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.REQUEST_REJECTED;
    } else {
      return false;
    }
  }

  isLastVendorPaymentRequested(eventPartyId: number): Observable<boolean> {
    return this.getVendorCurrentPayment(eventPartyId).map((lastPayment: Payment) => {
      return lastPayment && this.isPaymentRequested(lastPayment);
    });
  }

  isLastVendorPaymentPayRejected(eventPartyId: number): Observable<boolean> {
    return this.getVendorCurrentPayment(eventPartyId).map((lastPayment: Payment) => {
      return lastPayment && this.isPaymentPayRejected(lastPayment);
    });
  }

  private isPaymentRequested(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.REQUESTED;
  }

  get isLastPaymentRequestedOrRequestResentOrRequestRejected(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return lastPayment && (this.isPaymentRequested(lastPayment)
                              || this.isPaymentRequestResent(lastPayment)
                              || this.isPaymentRejected(lastPayment));
    });
  }

  isLastVendorPaymentRequestedOrRequestResentOrRequestRejected(eventPartyId): Observable<boolean> {
    return this.getVendorCurrentPayment(eventPartyId).map((lastPayment: Payment) => {
      return lastPayment && (this.isPaymentRequested(lastPayment)
        || this.isPaymentRequestResent(lastPayment)
        || this.isPaymentRejected(lastPayment));
    });
  }

  isRequestedOrResentRequestOrRequestRejectedOrError(paymentSummary: PaymentSummary): boolean {
    const statuses = [
      PaymentStatus.REQUESTED,
      PaymentStatus.RESENT_REQUEST,
      PaymentStatus.REQUEST_REJECTED
    ];
    return paymentSummary && (
      PaymentStatusUtils.isError(paymentSummary.paymentStatus)
      || statuses.some(status => status === paymentSummary.paymentStatus)
    );
  }

  get isEventLockdown(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return lastPayment && this.isThisEventLockdown(lastPayment);
    });
  }

  isEventPropertyLockdown(eventPartyId: number): Observable<boolean> {
    return this.getVendorCurrentPayment(eventPartyId).map((lastPayment: Payment) => {
      return lastPayment && this.isThisEventLockdown(lastPayment);
    });
  }

  private isPaymentRequestResent(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.RESENT_REQUEST;
  }

  private isThisEventLockdown(payment: Payment): boolean {
    return PaymentStatusUtils.isEditable(payment.status);
  }

  public isPaymentRequestedOrRequestResent(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status)
            || payment.status === PaymentStatus.REQUESTED
            || payment.status === PaymentStatus.RESENT_REQUEST;
  }

  public isPaymentPayRejected(payment: Payment): boolean {
    return payment.status === PaymentStatus.PAY_REJECTED;
  }

  getPrepayment(): Observable<Payment> {
    return this.latestPrepayment.asObservable();
  }

  getLatestVendorPrepayments(): Observable<Payment[]> {
    return this.latestVendorPrepayments.asObservable();
  }

  getLatestVendorPrepaymentsList(): Observable<List<Payment>> {
    return this.latestVendorPrepaymentsList.asObservable();
  }

  getVendorPrepayment(eventPartyId: number): Observable<Payment> {
    return this.latestVendorPrepayments
      .map(payments => payments != null ? payments.filter(payment => payment.eventPartyId === eventPartyId)[0] : null);
  }

  loadLatestPrepayment(eventId: number): Observable<Payment[]> {
    return this.paymentHttpService.getLatestVendorPrepayments(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PREPAYMENT'));
        this.logService.error(`Error getting Prepayments: ${error}`);
        return Observable.throw(error);
      })
      .map((payments: Payment[]) => {
        this.latestVendorPrepayments.next(payments);

        if (!payments || payments.length === 0) {
          this.latestPrepayment.next(null);
        }
        if (payments && payments.length === 1) {
          this.latestPrepayment.next(payments[0]);
        } else {
          this.latestPrepayment.next(null);
        }
        return payments;
      });
  }

  loadLatestPrepaymentByParty(eventId: number, eventPartyId: number): Observable<Payment[]> {
    return this.paymentHttpService.getLatestVendorPrepaymentsByParty(eventId, eventPartyId)
      .catch((error: any) => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PREPAYMENT'));
        this.logService.error(`Error getting Prepayments: ${error}`);
        return Observable.throw(error);
      })
      .map((payments: Payment[]) => {
        this.latestVendorPrepayments.next(payments);

        if (!payments || payments.length === 0) {
          this.latestPrepayment.next(null);
        }
        if (payments && payments.length === 1) {
          this.latestPrepayment.next(payments[0]);
        } else {
          this.latestPrepayment.next(null);
        }
        return payments;
      });
  }

  loadLatestPrepaymentList(eventId: number): Observable<List<Payment>> {
    return this.paymentHttpService.getLatestVendorPrepayments(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PREPAYMENT'));
        this.logService.error(`Error getting Prepayments: ${error}`);
        return Observable.throw(error);
      })
      .map((payments: List<Payment>) => {
        this.latestVendorPrepaymentsList.next(payments);
        return payments;
      });
  }

  loadFinalPaymentStatusList(eventId: number): Observable<Payment[]> {
    return this.paymentHttpService.getFinalPaymentStatusList(eventId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_FINAL_PAYMENT_STATUS'));
      this.logService.error(`Error getting list of final payment status: ${error}`);
      return Observable.throw(error);
    }).map((result: Payment[]) => {
      this.finalPaymentStatusList.next(result);
      return result;
    });
  }

  loadFinalPaymentStatusListByParty(eventId: number, eventPartyId: number): Observable<Payment[]> {
    return this.paymentHttpService.getFinalPaymentStatusListByParty(eventId, eventPartyId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_FINAL_PAYMENT_STATUS'));
      this.logService.error(`Error getting list of final payment status: ${error}`);
      return Observable.throw(error);
    }).map((result: Payment[]) => {
      this.finalPaymentStatusList.next(result);
      return result;
    });
  }

  getFinalPaymentStatusList(): Observable<Payment[]> {
    return this.finalPaymentStatusList.asObservable();
  }

  getVendorFinalPaymentStatusList(eventPartyId: number): Observable<Payment[]> {
    return this.finalPaymentStatusList
      .map(payments =>  payments != null ? payments.filter(payment => payment.eventPartyId === eventPartyId) : null);
  }

  resendPaymentRequest(eventId: number) {
    return this.paymentHttpService.resendPaymentRequest(eventId).catch(error => {
      this.logService.error(`Error resending payment request: ${error}`);
      return Observable.throw(error);
    }).map((result: Response) => {
      this.loadLatestFinalPayment(eventId).take(1).subscribe();
      this.loadFinalPaymentStatusList(eventId).take(1).subscribe();
      return result;
    });
  }

  getCurrentPayment(eventPartyId: number): Observable<Payment> {
    return this.getVendorInitialPayment(eventPartyId).flatMap((initialPayment) => {
      return this.getVendorFinalPayment(eventPartyId).map((finalPayment) => {
        return finalPayment ? finalPayment : (initialPayment ? initialPayment : null);
      });
    });
  }

  loadLatestInitialPaymentByParty(eventId: number, eventPartyId: number): Observable<Payment[]> {
    return this.paymentHttpService.getLatestInitialPaymentsByParty(eventId, eventPartyId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_LATEST_PAYMENT'));
      this.logService.error(`Error getting latest initial Payment by party: ${error}`);
      return Observable.throw(error);
    }).map((payments: Payment[]) => {
      this.latestInitialPayments.next(payments);
      this.latestVendorInitialPayment.next(null);

      if (payments && payments.length === 1) {
        this.latestVendorInitialPayment.next(payments[0]);
      }
      return payments;
    });
  }

  loadLatestInitialPayments(eventId: number): Observable<Payment[]> {
    return this.paymentHttpService.getLatestInitialPayments(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_LATEST_PAYMENT'));
        this.logService.error(`Error getting latest initial payments: ${error}`);
        return Observable.throw(error);
      })
      .map((payments: Payment[]) => {
        this.latestInitialPayments.next(payments);
        this.latestVendorInitialPayment.next(null);

        if (payments && payments.length === 1) {
          this.latestVendorInitialPayment.next(payments[0]);
        }
        return payments;
      });
  }

  getVendorInitialPayment(eventPartyId: number): Observable<Payment> {
    return this.latestInitialPayments.pipe(
      map((payments: Payment[]) => {
        if (payments) {
          return payments.find(payment => payment.eventPartyId === eventPartyId);
        }
        return null;
      })
    );
  }

  updateForecastedCommissionAmount(payment: Payment): Observable<any> {
    return this.paymentHttpService.updateForecastedCommissionAmount(payment).pipe(
      catchError(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_FORECASTED_COMMISSION_AMOUNT',
          `Error updating Forecasted Commission Amount: ${error}`);
        return Observable.throw(error);
      }),
      tap(() => {
        this.logService.info('Forecasted Commission Amount was updated successfully');
        this.flashMessagesService.success('EVENT.COMMISSION.FORECASTED_COMMISSION_UPDATED');
      })
    );
  }

  withdrawPaymentRequest(eventId: number, eventPartyId: number) {
    return this.paymentHttpService.withdrawPaymentRequest(eventId, eventPartyId)
      .catch(error => {
        this.logService.error(`Error withdrawing payment request. Event ID: ${eventId}. Event Party ID: ${eventPartyId}. Error: ${error}`);
        return Observable.throw(error);
      });
  }

  updateCalculatedPaymentWithTax(eventPartyId: number, calculatedPaymentWithTax: number): void {
    this._calculatedPaymentWithTaxByEventPartyId.set(eventPartyId, calculatedPaymentWithTax);
    this._calculatedPaymentWithTaxByEventPartyId$.next(this._calculatedPaymentWithTaxByEventPartyId);
  }

  getCalculatedPaymentWithTax(eventPartyId: number): Observable<number> {
    return this._calculatedPaymentWithTaxByEventPartyId$.map(calculatedPaymentWithTaxByEventPartyId => {
      return calculatedPaymentWithTaxByEventPartyId.get(eventPartyId);
    });
  }

  resetCalculatedPaymentWithTaxByEventPartyId(): void {
    this._calculatedPaymentWithTaxByEventPartyId.clear();
    this._calculatedPaymentWithTaxByEventPartyId$.next(this._calculatedPaymentWithTaxByEventPartyId);
  }

  bulkActionPayments(payments: Payment[]): Observable<BulkActionResponse[]> {
    return this.paymentHttpService.bulkPayments(payments)
      .catch(error => {
        return Observable.throw(error);
      }).pipe(
        tap(() => {
          const eventPartyIds = payments.map(p => p.eventPartyId);
          const eventPartyIdSet = new Set(eventPartyIds.filter(id => !!id));
          this.paymentSummaryStore.reloadPaymentSummary(eventPartyIdSet)
            .take(1)
            .subscribe();
        }),
        map((result: BulkActionResponse) => {
          let updatedResult = [];
          result.forEach((response: BulkActionResponse) => {
            if (response.error) {
              this.logService.info(`Error processing payment: ${response.error}`);
              if (response.error.includes('Unable to request final payment')) {
                updatedResult.push({ ...response, error: this.translateService.instant('MESSAGES.ERROR.OOIV_HEALTH') });
              } else if (response.error.includes('Legal Entity data for agency')) {
                updatedResult.push({ ...response, error: this.translateService.instant('MESSAGES.ERROR.MISSING_LEGAL_ENTITY_AGENCY') });
              } else if (response.error.includes('Legal Entity data for vendor')) {
                updatedResult.push({ ...response, error: this.translateService.instant('MESSAGES.ERROR.MISSING_LEGAL_ENTITY_VENDOR') });
              } else if (response.error.includes('Invalid Tax ID')) {
                updatedResult.push({ ...response, error: this.translateService.instant('MESSAGES.ERROR.INVALID_VAT_ID') });
              } else {
                updatedResult.push({ ...response, error: this.translateService.instant('MESSAGES.ERROR.ADD_PAYMENT') });
              }
            }
          });
          return updatedResult;
        })
      );
  }

  private addPaymentHelper(payment: Payment): Observable<any> {
    let obs: Observable<any>;
    if (payment.type && this.addPaymentStatuses.some(type => type === payment.type)) {
      obs = this.paymentHttpService.add(payment);
    } else {
      obs = this.paymentHttpService.request(payment);
    }
    return obs.pipe(this.reloadPaymentSummaryOnTap(payment));
  }

  private updatePaymentHelper(payment: Payment): Observable<any> {
    let obs: Observable<any>;
    if (payment.type && this.addPaymentStatuses.some(type => type === payment.type)) {
      obs = this.paymentHttpService.update(payment);
    } else {
      obs = this.paymentHttpService.request(payment);
    }
    return obs.pipe(this.reloadPaymentSummaryOnTap(payment));
  }

  private reloadPaymentSummaryOnTap(payment: Payment) {
    return tap(() => this.paymentSummaryStore.reloadPaymentSummary(payment.eventPartyId)
      .take(1)
      .subscribe()
    );
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/payment.store.ts